import { IPackageBuilderException, IPackageBuider } from 'app/shared/Domains/packageBuilder';
import { IPackageBuilderZone } from 'app/shared/Domains/packageBuilderZone';

/** 0=Sunday … 6=Saturday (matches console PackageAreaResolver). */
export const PACKAGE_DAYS_OF_WEEK: { label: string; value: number }[] = [
  { label: 'Sun', value: 0 },
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
];

/** One editable exception row in the package builder UI. */
export interface TimeBasedExceptionFormRow {
  label: string;
  startTime: Date | null;
  endTime: Date | null;
  selectedDays: number[];
  zones: any[];
}

export function cloneZonesForException(zones: any[]): any[] {
  if (!Array.isArray(zones)) {
    return [];
  }
  return zones.map((z) => ({
    ...z,
    IsSelect: 0,
  }));
}

export function hydrateExceptionZones(
  branchZones: any[],
  exceptionZones: IPackageBuilderZone[] | undefined,
): any[] {
  const selected = Array.isArray(exceptionZones)
    ? exceptionZones.filter((z) => Number(z.IsSelect) === 1)
    : [];
  return (branchZones || []).map((element) => {
    const match = selected.find((z) => String(z.id) === String(element.id));
    return {
      ...element,
      IsSelect: match ? 1 : 0,
    };
  });
}

/**
 * Format a picker Date (or HH:mm string) as local wall-clock HH:mm for UI display.
 */
export function formatTimeHHmm(value: Date | string | null | undefined): string {
  if (value == null || value === '') {
    return '';
  }
  if (typeof value === 'string') {
    const match = value.trim().match(/^(\d{1,2}):(\d{2})/);
    if (!match) {
      return value.trim();
    }
    return `${match[1].padStart(2, '0')}:${match[2]}`;
  }
  const hours = value.getHours().toString().padStart(2, '0');
  const minutes = value.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Format a picker Date as UTC time-of-day HH:mm for Mongo storage.
 * Example (Asia/Karachi UTC+5): local 09:00 → "04:00".
 * If value is already an HH:mm string, it is normalized as-is (assumed UTC).
 */
export function formatTimeHHmmUtc(value: Date | string | null | undefined): string {
  if (value == null || value === '') {
    return '';
  }
  if (typeof value === 'string') {
    const match = value.trim().match(/^(\d{1,2}):(\d{2})/);
    if (!match) {
      return value.trim();
    }
    return `${match[1].padStart(2, '0')}:${match[2]}`;
  }
  const hours = value.getUTCHours().toString().padStart(2, '0');
  const minutes = value.getUTCMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Parse a Mongo UTC HH:mm string into a Date for the time picker (shows local wall clock).
 * Example (Asia/Karachi UTC+5): "04:00" → picker shows 09:00.
 */
export function parseTimeHHmmToDate(value: string | null | undefined): Date | null {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const match = value.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) {
    return null;
  }
  const date = new Date();
  date.setUTCHours(Number(match[1]), Number(match[2]), 0, 0);
  return date;
}

/** Default clock time shown in empty access-window rows (12:00). */
export function createDefaultExceptionTime(): Date {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  return date;
}

export function createEmptyExceptionRow(branchZones: any[]): TimeBasedExceptionFormRow {
  return {
    label: '',
    startTime: createDefaultExceptionTime(),
    endTime: createDefaultExceptionTime(),
    selectedDays: [],
    zones: cloneZonesForException(branchZones),
  };
}

export function hydrateExceptionRowsFromPackage(
  branchZones: any[],
  exceptions: IPackageBuilderException[] | undefined,
): TimeBasedExceptionFormRow[] {
  if (!Array.isArray(exceptions) || exceptions.length === 0) {
    return [createEmptyExceptionRow(branchZones)];
  }
  return exceptions.map((ex) => ({
    label: (ex.label || '').trim(),
    startTime: parseTimeHHmmToDate(ex.startTime),
    endTime: parseTimeHHmmToDate(ex.endTime),
    selectedDays: Array.isArray(ex.daysOfWeek) ? [...ex.daysOfWeek] : [],
    zones: hydrateExceptionZones(branchZones, ex.zones),
  }));
}

/** Display title for an access-window row (uses label when set). */
export function getExceptionRowTitle(row: TimeBasedExceptionFormRow, index: number): string {
  const label = (row?.label || '').trim();
  if (label) {
    return label;
  }
  return `Time schedule ${index + 1}`;
}

export function buildExceptionFromUi(row: TimeBasedExceptionFormRow): IPackageBuilderException {
  const days =
    Array.isArray(row.selectedDays) && row.selectedDays.length > 0
      ? [...row.selectedDays].sort((a, b) => a - b)
      : undefined;

  return {
    label: (row.label || '').trim(),
    startTime: formatTimeHHmmUtc(row.startTime),
    endTime: formatTimeHHmmUtc(row.endTime),
    daysOfWeek: days,
    zones: (row.zones || []).map((z) => ({ ...z })),
  };
}

export function applyTimeBasedFieldsToPackage(
  pkg: IPackageBuider,
  input: {
    isTimeBased: boolean;
    exceptionRows: TimeBasedExceptionFormRow[];
  },
): void {
  pkg.isTimeBased = !!input.isTimeBased;
  if (!input.isTimeBased) {
    pkg.exceptions = [];
    return;
  }
  pkg.exceptions = (input.exceptionRows || []).map((row) => buildExceptionFromUi(row));
}

export function hasSelectedZones(zones: any[] | undefined | null): boolean {
  if (!Array.isArray(zones) || zones.length === 0) {
    return false;
  }
  return zones.some((z) => Number(z?.IsSelect) === 1);
}

/** Parse HH:mm to minutes from midnight. Returns null if invalid. */
export function timeHHmmToMinutes(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const match = String(value).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || hours > 23 || minutes > 59) {
    return null;
  }
  return hours * 60 + minutes;
}

/** Empty days = every day (0–6). */
function normalizeDays(days: number[] | undefined | null): number[] {
  if (!Array.isArray(days) || days.length === 0) {
    return [0, 1, 2, 3, 4, 5, 6];
  }
  return days.map((d) => Number(d)).filter((d) => Number.isFinite(d));
}

function daysIntersect(a: number[] | undefined | null, b: number[] | undefined | null): boolean {
  const left = new Set(normalizeDays(a));
  return normalizeDays(b).some((d) => left.has(d));
}

/** Half-open segments within one day. Overnight becomes two segments. */
function timeSegments(startMin: number, endMin: number): Array<[number, number]> {
  if (startMin === endMin) {
    return [];
  }
  if (startMin < endMin) {
    return [[startMin, endMin]];
  }
  // Overnight e.g. 22:00–06:00
  return [
    [startMin, 24 * 60],
    [0, endMin],
  ];
}

function segmentsOverlap(
  a: Array<[number, number]>,
  b: Array<[number, number]>,
): boolean {
  for (const [aStart, aEnd] of a) {
    for (const [bStart, bEnd] of b) {
      // Touching edges (14:00–16:00 and 16:00–18:00) are OK.
      if (aStart < bEnd && bStart < aEnd) {
        return true;
      }
    }
  }
  return false;
}

export function accessWindowsOverlap(
  a: { startTime: string; endTime: string; daysOfWeek?: number[] | null; label?: string },
  b: { startTime: string; endTime: string; daysOfWeek?: number[] | null; label?: string },
): boolean {
  if (!daysIntersect(a.daysOfWeek, b.daysOfWeek)) {
    return false;
  }
  const aStart = timeHHmmToMinutes(a.startTime);
  const aEnd = timeHHmmToMinutes(a.endTime);
  const bStart = timeHHmmToMinutes(b.startTime);
  const bEnd = timeHHmmToMinutes(b.endTime);
  if (aStart == null || aEnd == null || bStart == null || bEnd == null) {
    return false;
  }
  return segmentsOverlap(timeSegments(aStart, aEnd), timeSegments(bStart, bEnd));
}

export function validateTimeBasedAccess(input: {
  isTimeBased: boolean;
  exceptionRows: TimeBasedExceptionFormRow[];
  /** Top-level ACT / normal-mode zones. */
  normalZones?: any[] | null;
}): string | null {
  if (!input.isTimeBased) {
    return null;
  }
  const rows = input.exceptionRows || [];
  if (rows.length === 0) {
    return 'Please add at least one time schedule.';
  }
  const normalHasZones = hasSelectedZones(input.normalZones);
  const normalized: Array<{
    label: string;
    startTime: string;
    endTime: string;
    daysOfWeek?: number[];
  }> = [];

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const label = (row.label || '').trim();
    if (!label) {
      return `Please enter a schedule name for time schedule ${i + 1}.`;
    }
    const title = label;
    const start = formatTimeHHmmUtc(row.startTime);
    const end = formatTimeHHmmUtc(row.endTime);
    if (!start || !end) {
      return `Please enter start and end time for ${title}.`;
    }
    // Schedule zones are required only when always-allowed zones are empty
    // (allowed-only-in-schedule). When always-allowed zones are set, empty
    // schedule zones mean deny access during that schedule.
    if (!normalHasZones && !hasSelectedZones(row.zones)) {
      return `Please select at least one zone for ${title}.`;
    }
    normalized.push({
      label,
      startTime: start,
      endTime: end,
      daysOfWeek: row.selectedDays,
    });
  }

  for (let i = 0; i < normalized.length; i++) {
    for (let j = i + 1; j < normalized.length; j++) {
      if (accessWindowsOverlap(normalized[i], normalized[j])) {
        return `Time schedule "${normalized[i].label}" overlaps with "${normalized[j].label}".`;
      }
    }
  }
  return null;
}

const DAY_LABELS: Record<number, string> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

function formatDaysForDescription(days: number[] | undefined | null): string {
  const selected = Array.isArray(days)
    ? [...new Set(days.map((d) => Number(d)).filter((d) => Number.isFinite(d)))]
        .sort((a, b) => a - b)
    : [];
  if (selected.length === 0 || selected.length === 7) {
    return 'every day';
  }
  const names = selected.map((d) => DAY_LABELS[d] || String(d));
  if (names.length === 1) {
    return names[0];
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`;
  }
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

function formatZonesForDescription(zones: any[] | undefined | null): string {
  const selected = (zones || [])
    .filter((z) => Number(z?.IsSelect) === 1)
    .map((z) => String(z?.ZoneName || '').trim())
    .filter(Boolean);
  if (selected.length === 0) {
    return '';
  }
  if (selected.length === 1) {
    return `${selected[0]} only`;
  }
  if (selected.length === 2) {
    return `${selected[0]} and ${selected[1]}`;
  }
  return `${selected.slice(0, -1).join(', ')}, and ${selected[selected.length - 1]}`;
}

/**
 * Live plain-language summary of what one time schedule will do.
 */
export function describeAccessWindowSelection(
  row: TimeBasedExceptionFormRow,
  normalZones?: any[] | null,
): string {
  const start = formatTimeHHmm(row?.startTime);
  const end = formatTimeHHmm(row?.endTime);
  const daysText = formatDaysForDescription(row?.selectedDays);
  const zonesText = formatZonesForDescription(row?.zones);
  const normalHasZones = hasSelectedZones(normalZones);

  if (!start || !end) {
    return 'Choose a start and end time to preview what this schedule will do.';
  }

  const timePart = `from ${start} to ${end}`;
  const dayPart = daysText === 'every day' ? 'every day' : `on ${daysText}`;

  if (zonesText) {
    return `Members will be allowed ${timePart} ${dayPart} in ${zonesText}.`;
  }

  if (normalHasZones) {
    return `Members will not be allowed ${timePart} ${dayPart}. Outside this schedule, always-allowed zones still apply.`;
  }

  return `Select at least one zone so members can enter ${timePart} ${dayPart}.`;
}

function describeAccessWindowShort(
  row: TimeBasedExceptionFormRow,
  index: number,
  normalZones?: any[] | null,
): string {
  const label = (row?.label || '').trim() || `Time schedule ${index + 1}`;
  const start = formatTimeHHmm(row?.startTime);
  const end = formatTimeHHmm(row?.endTime);
  const daysText = formatDaysForDescription(row?.selectedDays);
  const zonesText = formatZonesForDescription(row?.zones);
  const normalHasZones = hasSelectedZones(normalZones);

  if (!start || !end) {
    return `"${label}" still needs a start and end time.`;
  }

  const timePart = `from ${start} to ${end}`;
  const dayPart = daysText === 'every day' ? 'every day' : `on ${daysText}`;

  if (zonesText) {
    return `During "${label}", members are allowed ${timePart} ${dayPart} in ${zonesText}.`;
  }
  if (normalHasZones) {
    return `During "${label}", members are not allowed ${timePart} ${dayPart}.`;
  }
  return `During "${label}", pick at least one zone for ${timePart} ${dayPart}.`;
}

/**
 * Combined live summary for all time schedules + always-allowed zones.
 */
export function describeAllAccessWindows(
  rows: TimeBasedExceptionFormRow[] | undefined | null,
  normalZones?: any[] | null,
): string {
  const list = Array.isArray(rows) ? rows : [];
  const normalZonesText = formatZonesForDescription(normalZones);
  const normalHasZones = hasSelectedZones(normalZones);

  let intro = '';
  if (normalHasZones && normalZonesText) {
    intro = `Outside time schedules, members use ${normalZonesText}.`;
  } else {
    intro = 'Members only have access during the time schedules below.';
  }

  if (list.length === 0) {
    return intro;
  }

  const windowLines = list.map((row, index) =>
    describeAccessWindowShort(row, index, normalZones),
  );
  return `${intro} ${windowLines.join(' ')}`;
}
